import React, { useEffect, useState } from 'react'
import { Button, DatePicker, Input } from '@/shared/ui'
import type {
  CoefficientDTO,
  DistanceRuleDTO,
  OrderCountRuleDTO,
  SurchargeDTO,
  TariffVersionDTO,
} from '@/shared/api'
import { TariffReadonlyView } from './TariffReadonlyView'
import {
  COEF_LABELS,
  CONDITION_LABELS,
  PERIOD_LABELS,
  WEEKDAY_LABELS,
  parseWeekdays,
  secondsToTime,
} from './formatters'

// Разбор строки ЧЧ:ММ (24-часовой формат) → секунды от начала суток.
// Некорректная или пустая строка → null.
const parseTime = (value: string): number | null => {
  const match = /^(\d{1,2}):(\d{1,2})$/.exec(value.trim())
  if (!match) return null
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (hours > 23 || minutes > 59) return null
  return hours * 3600 + minutes * 60
}

// Дата из сохранённых месяца/дня (год игнорируется — правило ежегодное)
const monthDayToDate = (month: number | null, day: number | null): Date | null => {
  if (!month || !day) return null
  return new Date(new Date().getFullYear(), month - 1, day)
}

interface TimeInputProps {
  value: number | null
  onChange: (value: number | null) => void
}

// Текстовое поле времени в 24-часовом формате с локальным черновиком,
// чтобы можно было свободно набирать значение и нормализовать его при blur.
const TimeInput: React.FC<TimeInputProps> = ({ value, onChange }) => {
  const [draft, setDraft] = useState(secondsToTime(value))

  useEffect(() => {
    setDraft(secondsToTime(value))
  }, [value])

  const commit = (raw: string) => {
    const parsed = parseTime(raw)
    onChange(parsed)
    setDraft(secondsToTime(parsed))
  }

  return (
    <input
      className="admin-input w-28"
      type="text"
      placeholder="ЧЧ:ММ"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={(e) => commit(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit((e.target as HTMLInputElement).value)
      }}
    />
  )
}

interface TariffEditorProps {
  version: TariffVersionDTO
  onChange: (version: TariffVersionDTO) => void
  /** Принудительный режим просмотра (иначе — по статусу версии). */
  readonly?: boolean
}

export const TariffEditor: React.FC<TariffEditorProps> = ({ version, onChange, readonly: forceReadonly }) => {
  const readonly = forceReadonly ?? (version.status === 1)

  // В просмотре — витрина без инпутов и кнопок: форма правки ниже остаётся
  // единственным местом, где данные можно изменить.
  if (readonly) return <TariffReadonlyView version={version} />

  const update = (patch: Partial<TariffVersionDTO>) => onChange({ ...version, ...patch })

  const setSurcharge = (i: number, patch: Partial<SurchargeDTO>) => {
    update({
      surcharges: version.surcharges.map((s, idx) =>
        idx === i ? { ...s, ...patch } : s
      ),
    })
  }

  const setCoefficient = (i: number, patch: Partial<CoefficientDTO>) => {
    update({
      coefficients: version.coefficients.map((c, idx) =>
        idx === i ? { ...c, ...patch } : c
      ),
    })
  }

  const setDistanceRule = (patch: Partial<DistanceRuleDTO>) => {
    const current = version.distance_rule ?? { included_km: 10, price_per_km: 30 }
    update({ distance_rule: { ...current, ...patch } })
  }

  const setRule = (i: number, patch: Partial<OrderCountRuleDTO>) => {
    update({
      order_count_rules: version.order_count_rules.map((r, idx) =>
        idx === i ? { ...r, ...patch } : r
      ),
    })
  }

  // Изменение приоритета на ±1 с последующей сортировкой (наибольший — первым)
  const changePriority = (i: number, delta: number) => {
    const next = version.coefficients.map((c, idx) =>
      idx === i ? { ...c, priority: c.priority + delta } : c
    )
    next.sort((a, b) => b.priority - a.priority)
    update({ coefficients: next })
  }

  // Переключение дня недели в CSV (Пн=1 … Вс=7)
  const toggleWeekday = (i: number, day: number) => {
    const coefficient = version.coefficients[i]
    if (!coefficient) return
    const days = parseWeekdays(coefficient.weekdays)
    const next = days.includes(day)
      ? days.filter((d) => d !== day)
      : [...days, day].sort((a, b) => a - b)
    setCoefficient(i, { weekdays: next.length ? next.join(',') : null })
  }

  // Поля параметра зависят от типа условия коэффициента
  const renderCoefficientParams = (c: CoefficientDTO, i: number) => {
    switch (c.condition_type) {
      case 1:
        return (
          <div className="flex flex-wrap items-center gap-1">
            <TimeInput
              value={c.time_from}
              onChange={(value) => setCoefficient(i, { time_from: value })}
            />
            <span className="text-gray-400">—</span>
            <TimeInput
              value={c.time_to}
              onChange={(value) => setCoefficient(i, { time_to: value })}
            />
          </div>
        )
      case 2:
        return (
          <div className="flex flex-wrap items-center gap-2">
            {WEEKDAY_LABELS.map((label, index) => {
              const day = index + 1
              const checked = parseWeekdays(c.weekdays).includes(day)
              return (
                <label key={day} className="flex items-center gap-1 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleWeekday(i, day)}
                  />
                  {label}
                </label>
              )
            })}
          </div>
        )
      case 3:
        return (
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-1">
              <span className="text-sm text-gray-500">Дата</span>
              <DatePicker
                value={monthDayToDate(c.date_start_month, c.date_start_day)}
                onChange={(date) =>
                  setCoefficient(i, {
                    date_start_day: date ? date.getDate() : null,
                    date_start_month: date ? date.getMonth() + 1 : null,
                  })
                }
                className="w-36"
              />
            </div>
            <span className="text-xs text-gray-400">Правило повторяется ежегодно</span>
          </div>
        )
      case 4:
        return (
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex flex-wrap items-center gap-1">
                <span className="text-sm text-gray-500">С</span>
                <DatePicker
                  value={monthDayToDate(c.date_start_month, c.date_start_day)}
                  onChange={(date) =>
                    setCoefficient(i, {
                      date_start_day: date ? date.getDate() : null,
                      date_start_month: date ? date.getMonth() + 1 : null,
                    })
                  }
                  className="w-36"
                />
              </div>
              <div className="flex flex-wrap items-center gap-1">
                <span className="text-sm text-gray-500">По</span>
                <DatePicker
                  value={monthDayToDate(c.date_end_month, c.date_end_day)}
                  onChange={(date) =>
                    setCoefficient(i, {
                      date_end_day: date ? date.getDate() : null,
                      date_end_month: date ? date.getMonth() + 1 : null,
                    })
                  }
                  className="w-36"
                />
              </div>
            </div>
            <span className="text-xs text-gray-400">Правило повторяется ежегодно</span>
          </div>
        )
      default:
        return null
    }
  }

  return (
    <div className="space-y-6">
      <section className="admin-card p-4">
        <h3 className="mb-3 font-semibold">Расчёт</h3>
        <div className="grid grid-cols-3 gap-3">
          <Input
            label="Базовая ставка"
            type="number"
            value={version.base_rate}
            onChange={(e) => update({ base_rate: Number(e.target.value) })}
          />
          <Input
            label="Минимальная сумма"
            type="number"
            value={version.min_amount ?? ''}
            onChange={(e) =>
              update({ min_amount: e.target.value === '' ? null : Number(e.target.value) })
            }
          />
          <Input
            label="Максимальная сумма"
            type="number"
            value={version.max_amount ?? ''}
            onChange={(e) =>
              update({ max_amount: e.target.value === '' ? null : Number(e.target.value) })
            }
          />
        </div>
      </section>

      <section className="admin-card p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-semibold">Надбавка за расстояние</h3>
          {version.distance_rule ? (
            <Button
              variant="danger"
              onClick={() => update({ distance_rule: null })}
            >
              Убрать
            </Button>
          ) : (
            <Button
              variant="secondary"
              onClick={() =>
                update({ distance_rule: { included_km: 10, price_per_km: 30 } })
              }
            >
              Добавить
            </Button>
          )}
        </div>
        {version.distance_rule ? (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Включено в базовую ставку, км"
                type="number"
                min={0}
                step="0.1"
                value={version.distance_rule.included_km}
                onChange={(e) =>
                  setDistanceRule({ included_km: Number(e.target.value) })
                }
              />
              <Input
                label="Цена за каждый следующий км, ₽"
                type="number"
                min={0}
                step="0.01"
                value={version.distance_rule.price_per_km}
                onChange={(e) =>
                  setDistanceRule({ price_per_km: Number(e.target.value) })
                }
              />
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Пример: 13 км при включённых 10 км и 30 ₽/км → надбавка 90 ₽.
            </p>
          </>
        ) : (
          <p className="text-sm text-gray-500">
            Не применяется: стоимость зависит только от базовой ставки и других условий.
          </p>
        )}
      </section>

      <section className="admin-card p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-semibold">Фиксированные надбавки</h3>
          <Button
            variant="secondary"
            onClick={() =>
              update({
                surcharges: [
                  ...version.surcharges,
                  { condition_type: 0, condition_operator: 0, condition_value: null, amount: 0 },
                ],
              })
            }
          >
            Добавить
          </Button>
        </div>
        {/* Шапка колонок: без неё третье поле читается как «ещё одно значение» */}
        {version.surcharges.length > 0 && (
          <div className="mb-2 grid grid-cols-4 gap-2 text-xs uppercase text-gray-500">
            <span>Тип условия</span>
            <span>Значение</span>
            <span>Надбавка</span>
            <span aria-hidden="true" />
          </div>
        )}
        {version.surcharges.map((s, i) => (
          <div key={i} className="mb-2 grid grid-cols-4 gap-2">
            <select
              className="admin-input"
              value={s.condition_type}
              onChange={(e) => setSurcharge(i, { condition_type: Number(e.target.value) })}
            >
              {Object.entries(CONDITION_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
            <input
              className="admin-input"
              placeholder="Значение"
              value={s.condition_value ?? ''}
              onChange={(e) => setSurcharge(i, { condition_value: e.target.value })}
            />
            <input
              className="admin-input"
              type="number"
              value={s.amount}
              onChange={(e) => setSurcharge(i, { amount: Number(e.target.value) })}
            />
            <Button
              variant="danger"
              onClick={() =>
                update({
                  surcharges: version.surcharges.filter((_, idx) => idx !== i),
                })
              }
            >
              Удалить
            </Button>
          </div>
        ))}
      </section>

      <section className="admin-card p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-semibold">Надбавки за количество</h3>
          <Button
            variant="secondary"
            onClick={() =>
              update({
                order_count_rules: [
                  ...version.order_count_rules,
                  { threshold: 10, period: 1, amount: 0 },
                ],
              })
            }
          >
            Добавить
          </Button>
        </div>
        {/* Шапка колонок: порог, период и сумма надбавки */}
        {version.order_count_rules.length > 0 && (
          <div className="mb-2 grid grid-cols-4 gap-2 text-xs uppercase text-gray-500">
            <span>Порог</span>
            <span>Период</span>
            <span>Надбавка</span>
            <span aria-hidden="true" />
          </div>
        )}
        {version.order_count_rules.map((r, i) => (
          <div key={i} className="mb-2 grid grid-cols-4 gap-2">
            <input
              className="admin-input"
              type="number"
              value={r.threshold}
              onChange={(e) => setRule(i, { threshold: Number(e.target.value) })}
            />
            <select
              className="admin-input"
              value={r.period}
              onChange={(e) => setRule(i, { period: Number(e.target.value) })}
            >
              {Object.entries(PERIOD_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
            <input
              className="admin-input"
              type="number"
              value={r.amount}
              onChange={(e) => setRule(i, { amount: Number(e.target.value) })}
            />
            <Button
              variant="danger"
              onClick={() =>
                update({
                  order_count_rules: version.order_count_rules.filter((_, idx) => idx !== i),
                })
              }
            >
              Удалить
            </Button>
          </div>
        ))}
      </section>

      <section className="admin-card p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-semibold">Коэффициенты</h3>
          <Button
            variant="secondary"
            onClick={() =>
              update({
                coefficients: [
                  ...version.coefficients,
                  {
                    condition_type: 0,
                    coefficient: 1,
                    priority: 0,
                    time_from: null,
                    time_to: null,
                    weekdays: null,
                    date_start_month: null,
                    date_start_day: null,
                    date_end_month: null,
                    date_end_day: null,
                  },
                ],
              })
            }
          >
            Добавить
          </Button>
        </div>
        <p className="mb-3 text-xs text-gray-500">
          Применяется одно правило с наибольшим числовым приоритетом. При равном приоритете —
          первое в списке.
        </p>
        {/* Шапка колонок повторяет порядок полей в строке: тип правила → условия
            применения → коэффициент → приоритет. Ширины фиксированы (кроме колонки
            условий), иначе подписи «уезжают» над чужими полями. */}
        {version.coefficients.length > 0 && (
          <div className="mb-2 flex flex-wrap items-center gap-2 text-xs uppercase text-gray-500">
            <span className="w-40 shrink-0">Тип правила</span>
            <span className="min-w-[8rem] flex-1">Условия применения</span>
            <span className="w-24 shrink-0 text-right">Коэффициент</span>
            <span className="w-24 shrink-0 text-right">Приоритет</span>
            {/* Место под стрелки ↑/↓ и кнопку «Удалить» */}
            <span className="w-48 shrink-0" aria-hidden="true" />
          </div>
        )}
        {version.coefficients.map((c, i) => (
          <div key={i} className="mb-2 flex flex-wrap items-center gap-2">
            <select
              className="admin-input w-40 shrink-0"
              value={c.condition_type}
              onChange={(e) => setCoefficient(i, { condition_type: Number(e.target.value) })}
            >
              {Object.entries(COEF_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
            {/* Параметры условия идут сразу за типом правила — так же, как в витрине */}
            <div className="flex min-w-[8rem] flex-1 flex-wrap items-center gap-2">
              {renderCoefficientParams(c, i)}
            </div>
            <input
              className="admin-input w-24 shrink-0 text-right"
              type="number"
              step="0.001"
              value={c.coefficient}
              onChange={(e) => setCoefficient(i, { coefficient: Number(e.target.value) })}
            />
            <input
              className="admin-input w-24 shrink-0 text-right"
              type="number"
              placeholder="Приоритет"
              value={c.priority}
              onChange={(e) => setCoefficient(i, { priority: Number(e.target.value) })}
            />
            {/* Стрелки меняют приоритет соседнего поля, поэтому стоят сразу за ним.
                Фиксированная ширина нужна, чтобы шапка совпадала со строкой. */}
            <div className="flex w-48 shrink-0 items-center gap-1">
              <button
                type="button"
                className="admin-button admin-button-secondary px-2 py-1"
                title="Повысить приоритет"
                onClick={() => changePriority(i, 1)}
              >
                ↑
              </button>
              <button
                type="button"
                className="admin-button admin-button-secondary px-2 py-1"
                title="Понизить приоритет"
                onClick={() => changePriority(i, -1)}
              >
                ↓
              </button>
              <Button
                variant="danger"
                onClick={() =>
                  update({
                    coefficients: version.coefficients.filter((_, idx) => idx !== i),
                  })
                }
              >
                Удалить
              </Button>
            </div>
          </div>
        ))}
      </section>
    </div>
  )
}
